import React, { useEffect, useState } from "react";
import { Instagram, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { ssgData } from "@/lib/ssg";
import { INSTAGRAM_URL } from "@/lib/assets";

/** Curated Instagram highlights, managed from the admin dashboard. */
export const InstagramStrip = ({ limit = 3 }) => {
  const [items, setItems] = useState(() => ssgData("instagram") || []);

  useEffect(() => {
    api.get("/instagram").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  if (items.length === 0) return null;
  const shown = items.slice(0, limit);

  return (
    <section className="py-24 md:py-32 bg-[#F4EFE6]/50" data-testid="instagram-strip">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="eyebrow text-[#A39171] mb-4">On Instagram</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              Awareness work, <span className="italic text-[#E07A5F]">off the clinic floor</span>.
            </h2>
            <p className="mt-5 text-[#5C524E] leading-relaxed">
              Camps, talks, campus drives and the small everyday reminders we share with Bhavnagar.
            </p>
          </div>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow"
            className="inline-flex items-center gap-2 rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-7 h-12 font-medium transition-colors shrink-0"
          >
            <Instagram className="w-4 h-4" /> Follow @manas_hospital_
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((p, i) => {
            const Card = p.post_url ? "a" : "div";
            const linkProps = p.post_url
              ? { href: p.post_url, target: "_blank", rel: "noopener noreferrer" }
              : {};
            return (
              <Card
                key={p.id}
                {...linkProps}
                data-testid={`instagram-post-${i}`}
                className="group block bg-[#FDFBF7] rounded-3xl overflow-hidden border border-[#A39171]/15 hover:shadow-[0_24px_60px_rgba(163,145,113,0.15)] transition-all duration-500 hover:-translate-y-1"
              >
                <div className="aspect-square overflow-hidden relative">
                  <img
                    src={p.image_url}
                    alt={p.caption || "Manas Hospital on Instagram"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <span className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#FDFBF7]/90 flex items-center justify-center text-[#2D2623]">
                    <Instagram className="w-4 h-4" />
                  </span>
                </div>
                {p.caption && (
                  <div className="p-6">
                    <p className="text-[#2D2623] leading-relaxed line-clamp-3">{p.caption}</p>
                    {p.post_url && (
                      <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#5C524E] font-medium group-hover:text-[#E07A5F] transition-colors">
                        View on Instagram <ArrowUpRight className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default InstagramStrip;
